import React, { useState, useMemo } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    UserRound,
    Phone,
    Mail,
    MapPin,
    BriefcaseBusiness,
    GraduationCap,
    IndianRupee,
    FileText,
    Upload,
    X,
    CheckCircle2,
    ArrowRight,
    Building2,
    Globe2,
    ShieldCheck,
} from "lucide-react";

import { saveTrainer, getAllTrainer } from "../services/trainerService";
import SkillPicker, { CheckOption } from "./SkillPicker";
import SearchableSelect from "./SearchableSelect";
import "./Page.css";

/* =========================
   PDF WORKER (CDN - version auto match)
========================= */
pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/* =========================
   CONSTANTS
========================= */
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_REGEX = /^[6-9]\d{9}$/;

const TECHNICAL_TYPE = "Technical / Engineering Trainer";

const professionalTypes = [
    "IT / Corporate Trainer",
    TECHNICAL_TYPE,
    "NEET Faculty",
    "JEE Faculty",
    "Home Tutor",
];

const technologyOptionsByType = {
    "IT / Corporate Trainer": [
        // Programming Languages
        "C Programming", "C++ Programming", "Java", "Advanced Java", "Python",
        "C#", "JavaScript", "TypeScript", "PHP", "SQL Programming",

        // DSA & Computer Science
        "Data Structures", "Data Structures and Algorithms (DSA)", "Algorithms",
        "Operating Systems", "DBMS", "Computer Networks", "Computer Organization",
        "Compiler Design", "Theory of Computation", "Software Engineering",
        "Object-Oriented Programming", "System Design",

        // Web Development
        "HTML", "CSS", "Bootstrap", "Tailwind CSS", "React JS", "Angular",
        "Vue JS", "Node JS", "Express JS", "MERN Stack", "MEAN Stack",
        "Full Stack Web Development", "Frontend Development", "Backend Development",
        "REST API Development", "Next.js",

        // Java Development
        "Core Java", "JDBC", "Servlets and JSP", "Hibernate", "Spring Framework",
        "Spring Boot", "Microservices", "Java Full Stack Development",

        // Python Development
        "Core Python", "Advanced Python", "Django", "Flask", "FastAPI",

        // Mobile App Development
        "Android Development", "Kotlin", "Flutter", "React Native",

        // Database Technologies
        "MySQL", "PostgreSQL", "Oracle Database", "MongoDB",
        "Microsoft SQL Server", "Redis",

        // Data Science & AI
        "Data Science", "Data Analytics", "Data Analysis with Python",
        "Statistics for Data Science", "Machine Learning", "Deep Learning",
        "Artificial Intelligence", "Generative AI", "Natural Language Processing",
        "Computer Vision", "Large Language Models (LLMs)", "Prompt Engineering",
        "TensorFlow", "PyTorch", "OpenCV",

        // BI & Analytics
        "Microsoft Excel", "Advanced Excel", "Power BI", "Tableau",
        "Business Analytics", "SQL for Data Analytics",

        // Cloud Computing
        "Cloud Computing", "Amazon Web Services (AWS)", "Microsoft Azure",
        "Google Cloud Platform (GCP)", "AWS Solutions Architecture",
        "Azure Cloud Administration",

        // DevOps & Deployment
        "DevOps", "Git", "GitHub", "GitLab", "Linux Administration", "Docker",
        "Kubernetes", "Jenkins", "CI/CD", "Terraform", "Ansible",
        "Infrastructure as Code",

        // Cybersecurity & Networking
        "Cyber Security", "Ethical Hacking", "Network Security",
        "Information Security", "Penetration Testing", "Digital Forensics",
        "CompTIA Security+ Preparation", "CCNA", "Network Administration",

        // Testing & Tools
        "Software Testing", "Manual Testing", "Automation Testing", "Selenium",
        "API Testing", "Postman", "JUnit", "Agile and Scrum",

        // Other IT Courses
        "Internet of Things (IoT)", "Blockchain Technology", "Web3 Fundamentals",
        "RPA", "Robotic Process Automation", "IT Support",
        "Technical Interview Preparation", "Programming Aptitude",
        "GATE CSE Preparation",
    ],

    "Civil Engineering": [
        "Engineering Mechanics", "Building Materials", "Building Construction",
        "Surveying", "Advanced Surveying", "Geomatics Engineering",
        "Structural Analysis", "Structural Engineering",
        "Reinforced Cement Concrete (RCC) Design", "Steel Structure Design",
        "Prestressed Concrete", "Concrete Technology",
        "Construction Materials Testing", "Geotechnical Engineering",
        "Soil Mechanics", "Foundation Engineering", "Fluid Mechanics",
        "Hydraulics Engineering", "Hydrology", "Irrigation Engineering",
        "Water Resources Engineering", "Environmental Engineering",
        "Water Supply Engineering", "Wastewater Engineering",
        "Solid Waste Management", "Transportation Engineering",
        "Highway Engineering", "Railway Engineering", "Airport Engineering",
        "Traffic Engineering", "Construction Management",
        "Project Planning and Management", "Estimation and Costing",
        "Quantity Surveying", "Contracts and Specifications", "AutoCAD Civil",
        "Revit Architecture", "Revit Structure", "STAAD Pro", "ETABS", "SAFE",
        "Civil 3D", "Building Information Modeling (BIM)", "Primavera P6",
        "MS Project", "Green Building Technology", "Earthquake Resistant Design",
        "Bridge Engineering", "Tunnel Engineering",
        "GATE Civil Engineering Preparation",
    ],

    "Mechanical Engineering": [
        "Engineering Mechanics", "Engineering Graphics", "Engineering Materials",
        "Strength of Materials", "Theory of Machines", "Machine Design",
        "Design of Machine Elements", "Engineering Thermodynamics",
        "Applied Thermodynamics", "Fluid Mechanics", "Fluid Machinery",
        "Heat Transfer", "Refrigeration and Air Conditioning",
        "Internal Combustion Engines", "Automobile Engineering",
        "Electric Vehicle Technology", "Manufacturing Processes",
        "Production Engineering", "Production Planning and Control",
        "Industrial Engineering", "Operations Research", "Quality Control",
        "Metrology and Measurements", "CNC Programming", "CNC Machining", "CAD",
        "CAM", "CAD / CAM", "SolidWorks", "CATIA", "AutoCAD Mechanical",
        "Creo Parametric", "Siemens NX", "ANSYS", "Finite Element Analysis (FEA)",
        "Computational Fluid Dynamics (CFD)", "Robotics", "Industrial Automation",
        "Mechatronics", "Additive Manufacturing", "3D Printing",
        "Welding Technology", "Casting and Forming", "Maintenance Engineering",
        "Lean Manufacturing", "Six Sigma", "Supply Chain Management",
        "GATE Mechanical Engineering Preparation",
    ],

    "Electrical Engineering": [
        "Basic Electrical Engineering", "Circuit Theory", "Network Theory",
        "Electrical Machines", "DC Machines", "AC Machines", "Transformers",
        "Power Systems", "Power System Analysis", "Power Generation",
        "Power Transmission and Distribution", "Power System Protection",
        "Switchgear and Protection", "Control Systems", "Power Electronics",
        "Electrical Measurements", "Electrical Instrumentation",
        "Electrical Drives", "High Voltage Engineering",
        "Electrical Machine Design", "PLC Programming", "SCADA Systems",
        "Industrial Automation", "Renewable Energy", "Solar Power Systems",
        "Wind Energy Systems", "Smart Grid Technology",
        "Electric Vehicle Technology", "Battery Management Systems", "MATLAB",
        "Simulink", "ETAP", "Electrical CAD",
        "GATE Electrical Engineering Preparation",
    ],

    "Electronics & Communication": [
        "Basic Electronics", "Analog Electronics", "Digital Electronics",
        "Electronic Devices and Circuits", "Circuit Design",
        "Signals and Systems", "Digital Signal Processing",
        "Analog Communication", "Digital Communication",
        "Wireless Communication", "Optical Fiber Communication",
        "Microwave Engineering", "Antenna and Wave Propagation",
        "Electromagnetic Theory", "Microprocessors", "8085 Microprocessor",
        "8086 Microprocessor", "Microcontrollers", "Embedded Systems",
        "Embedded C Programming", "ARM Microcontrollers", "VLSI Design",
        "Verilog HDL", "VHDL", "ASIC Design", "FPGA Design", "PCB Design",
        "Internet of Things (IoT)", "Arduino Programming", "Raspberry Pi",
        "ESP32 Development", "Robotics", "Sensors and Transducers",
        "Control Systems", "MATLAB", "LabVIEW", "Electronic Measurements",
        "GATE ECE Preparation",
    ],

    "Common / Foundation": [
        "Engineering Mathematics", "Linear Algebra", "Calculus",
        "Differential Equations", "Probability and Statistics",
        "Numerical Methods", "Discrete Mathematics", "Engineering Physics",
        "Engineering Chemistry", "Engineering Drawing", "Technical Drawing",
        "Communication Skills", "Technical Writing", "Professional English",
        "Quantitative Aptitude", "Logical Reasoning", "Verbal Ability",
        "Problem Solving", "Interview Preparation", "Resume Building",
        "Group Discussion", "Personality Development",
        "Campus Placement Preparation", "GATE Preparation",
        "Competitive Exam Preparation", "Research Methodology",
        "Project Management", "Entrepreneurship Development",
        "Soft Skills Training", "Microsoft Office", "MS Word", "MS Excel",
        "MS PowerPoint",
    ],

    "NEET Faculty": ["Physics", "Chemistry", "Biology", "Botany", "Zoology"],
    "JEE Faculty": ["Physics", "Chemistry", "Mathematics"],
    "Home Tutor": [
        "Mathematics", "Science", "English", "Hindi", "Social Studies",
        "Physics", "Chemistry", "Biology", "Computer Science",
        "Sanskrit", "Environmental Studies",
    ],
};

/* Branch-wise subjects (subject -> courses) for Technical / Engineering Trainer */
const technicalSubjectGroups = [
    {
        title: "Civil Engineering",
        items: [
            { subject: "Structural Engineering", courses: ["Structural Analysis", "RCC Design", "Steel Structure Design"] },
            { subject: "Surveying", courses: ["Basic Surveying", "Advanced Surveying", "Total Station"] },
            { subject: "Concrete Technology", courses: ["Concrete Mix Design", "Advanced Concrete Technology"] },
            { subject: "Geotechnical Engineering", courses: ["Soil Mechanics", "Foundation Engineering"] },
            { subject: "Fluid Mechanics", courses: ["Fluid Mechanics", "Hydraulics Engineering"] },
            { subject: "Transportation Engineering", courses: ["Highway Engineering", "Railway Engineering"] },
            { subject: "Environmental Engineering", courses: ["Water Supply Engineering", "Wastewater Treatment"] },
            { subject: "Construction Management", courses: ["Project Management", "Construction Planning"] },
            { subject: "Estimation & Costing", courses: ["Quantity Surveying", "Cost Estimation"] },
            { subject: "AutoCAD (Civil)", courses: ["AutoCAD 2D", "AutoCAD 3D"] },
            { subject: "STAAD Pro", courses: ["STAAD Pro Basics", "Structural Design with STAAD Pro"] },
            { subject: "Revit", courses: ["Revit Architecture", "Revit Structure", "BIM Modeling"] },
        ],
    },
    {
        title: "Computer Science Engineering",
        items: [
            { subject: "Programming in C", courses: ["C Programming for Beginners", "Advanced C Programming"] },
            { subject: "Data Structures", courses: ["Data Structures in C", "Data Structures in C++", "DSA with Java"] },
            { subject: "Operating Systems", courses: ["Operating System Fundamentals", "Linux Administration"] },
            { subject: "DBMS", courses: ["DBMS Fundamentals", "SQL", "MySQL", "MongoDB"] },
            { subject: "Computer Networks", courses: ["Computer Networking", "CCNA Fundamentals", "Network Security"] },
            { subject: "Compiler Design", courses: ["Compiler Design Fundamentals", "Lexical Analysis and Parsing"] },
            { subject: "Theory of Computation", courses: ["Automata Theory", "Formal Languages", "Computability Theory"] },
            { subject: "Software Engineering", courses: ["Software Development Life Cycle", "Agile and Scrum", "Software Testing"] },
            { subject: "Computer Architecture", courses: ["Computer Organization", "Digital Logic Design", "Microprocessor Fundamentals"] },
            { subject: "Web Development", courses: ["HTML, CSS and JavaScript", "React.js", "Node.js and Express.js", "MERN Stack"] },
        ],
    },
    {
        title: "Mechanical Engineering",
        items: [
            { subject: "Thermodynamics", courses: ["Engineering Thermodynamics", "Applied Thermodynamics"] },
            { subject: "Strength of Materials", courses: ["Mechanics of Materials", "Advanced Strength of Materials"] },
            { subject: "Fluid Machinery", courses: ["Hydraulic Machines", "Pumps and Turbines"] },
            { subject: "Heat Transfer", courses: ["Heat Transfer Fundamentals", "Advanced Heat Transfer"] },
            { subject: "Machine Design", courses: ["Machine Design Fundamentals", "Mechanical Component Design"] },
            { subject: "Manufacturing Processes", courses: ["Manufacturing Technology", "CNC Machining"] },
            { subject: "CAD / CAM", courses: ["CAD Fundamentals", "CAM Programming"] },
            { subject: "SolidWorks", courses: ["SolidWorks Essentials", "3D Part and Assembly Design"] },
            { subject: "CATIA", courses: ["CATIA Part Design", "CATIA Assembly Design"] },
            { subject: "ANSYS", courses: ["ANSYS Workbench", "Finite Element Analysis"] },
            { subject: "Automobile Engineering", courses: ["Automotive Systems", "Electric Vehicle Technology"] },
            { subject: "Robotics", courses: ["Robotics Fundamentals", "Industrial Robotics"] },
            { subject: "Production Engineering", courses: ["Production Planning", "Lean Manufacturing"] },
        ],
    },
    {
        title: "Electrical Engineering",
        items: [
            { subject: "Circuit Theory", courses: ["Electrical Circuit Analysis", "Network Theory"] },
            { subject: "Electrical Machines", courses: ["DC Machines", "AC Machines", "Transformers"] },
            { subject: "Power Systems", courses: ["Power Generation", "Power System Analysis", "Power Distribution"] },
            { subject: "Control Systems", courses: ["Control System Fundamentals", "Industrial Control Systems"] },
            { subject: "Power Electronics", courses: ["Power Semiconductor Devices", "Power Converter Design"] },
            { subject: "Electrical Measurements", courses: ["Electrical Instrumentation", "Measurement Techniques"] },
            { subject: "PLC / SCADA", courses: ["PLC Programming", "SCADA Systems", "Industrial Automation"] },
            { subject: "Renewable Energy", courses: ["Solar Energy Systems", "Wind Energy Technology"] },
        ],
    },
    {
        title: "Electronics & Communication",
        items: [
            { subject: "Analog Electronics", courses: ["Analog Circuit Design", "Electronic Devices"] },
            { subject: "Digital Electronics", courses: ["Digital Logic Design", "Digital System Design"] },
            { subject: "Microprocessors", courses: ["8085 Microprocessor", "8086 Microprocessor"] },
            { subject: "Embedded Systems", courses: ["Embedded C", "ARM Microcontrollers"] },
            { subject: "VLSI Design", courses: ["VLSI Fundamentals", "Verilog HDL", "ASIC Design"] },
            { subject: "Signals & Systems", courses: ["Signal Processing Fundamentals", "Digital Signal Processing"] },
            { subject: "Communication Systems", courses: ["Analog Communication", "Digital Communication"] },
            { subject: "IoT", courses: ["Internet of Things", "IoT with ESP32"] },
            { subject: "Arduino / Raspberry Pi", courses: ["Arduino Programming", "Raspberry Pi Projects"] },
        ],
    },
    {
        title: "Common / Foundation",
        items: [
            { subject: "Engineering Mathematics", courses: ["Linear Algebra", "Calculus", "Probability and Statistics", "Numerical Methods"] },
            { subject: "Engineering Physics", courses: ["Engineering Physics Fundamentals", "Applied Physics"] },
            { subject: "Engineering Chemistry", courses: ["Engineering Chemistry Fundamentals", "Applied Chemistry"] },
            { subject: "Engineering Drawing", courses: ["Engineering Graphics", "Technical Drawing"] },
            { subject: "Communication Skills", courses: ["English Communication", "Technical Writing", "Interview Skills"] },
            { subject: "Aptitude & Reasoning", courses: ["Quantitative Aptitude", "Logical Reasoning", "Verbal Ability"] },
            { subject: "GATE Preparation", courses: ["GATE CSE", "GATE Civil", "GATE Mechanical", "GATE Electrical", "GATE ECE"] },
        ],
    },
];

/*
  Engineering ke liye SkillPicker sections:
  - har branch ek tab/section
  - har subject ek heading, uske courses checkbox
  - technologyOptionsByType ki branch-wise flat list ke jo topics
    courses mein nahi hain, wo "Other Topics" mein judte hain
*/
const engineeringSections = technicalSubjectGroups.map((branch) => {
    const covered = new Set();

    const blocks = branch.items.map(({ subject, courses }) => {
        courses.forEach((course) => covered.add(course));
        return { heading: subject, items: courses };
    });

    const extra = (technologyOptionsByType[branch.title] || []).filter(
        (topic) => !covered.has(topic)
    );

    if (extra.length > 0) {
        blocks.push({ heading: "Other Topics", items: extra });
    }

    return { id: branch.title, title: branch.title, blocks };
});

const classOptions = [
    "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6",
    "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12",
    "Dropper / Competitive Exam",
];

const examOptions = [
    "NEET UG", "NEET Foundation", "JEE Main", "JEE Advanced",
    "JEE Foundation", "Other Competitive Exams",
];

const teachingModeOptions = ["Online", "Offline / Classroom", "Home Tuition"];

const states = {
    "Andhra Pradesh": [
        "Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Tirupati",
        "Rajahmundry", "Kakinada", "Kadapa", "Anantapur", "Eluru", "Ongole",
        "Machilipatnam", "Srikakulam", "Vizianagaram",
    ],
    "Arunachal Pradesh": [
        "Itanagar", "Naharlagun", "Tawang", "Pasighat", "Ziro", "Bomdila",
        "Tezu", "Aalo", "Roing", "Namsai",
    ],
    "Assam": [
        "Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Nagaon", "Tinsukia",
        "Tezpur", "Bongaigaon", "Dhubri", "Diphu", "Sivasagar", "North Lakhimpur",
    ],
    "Bihar": [
        "Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Darbhanga", "Purnia",
        "Bihar Sharif", "Ara", "Begusarai", "Katihar", "Munger", "Chhapra",
        "Samastipur", "Sasaram", "Hajipur", "Motihari",
    ],
    "Chhattisgarh": [
        "Raipur", "Bhilai", "Bilaspur", "Korba", "Durg", "Rajnandgaon",
        "Jagdalpur", "Raigarh", "Ambikapur", "Dhamtari", "Mahasamund", "Kanker",
    ],
    "Goa": [
        "Panaji", "Margao", "Vasco da Gama", "Mapusa", "Ponda", "Bicholim",
        "Curchorem", "Canacona",
    ],
    "Gujarat": [
        "Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar",
        "Junagadh", "Gandhinagar", "Anand", "Nadiad", "Morbi", "Mehsana",
        "Bharuch", "Navsari", "Valsad", "Porbandar", "Palanpur", "Godhra",
        "Surendranagar", "Vapi",
    ],
    "Haryana": [
        "Gurugram", "Faridabad", "Panipat", "Ambala", "Hisar", "Karnal",
        "Rohtak", "Sonipat", "Yamunanagar", "Panchkula", "Sirsa", "Rewari",
        "Bhiwani", "Kurukshetra", "Jind", "Kaithal",
    ],
    "Himachal Pradesh": [
        "Shimla", "Dharamshala", "Solan", "Mandi", "Kullu", "Manali",
        "Hamirpur", "Una", "Bilaspur", "Chamba", "Nahan", "Palampur",
    ],
    "Jharkhand": [
        "Ranchi", "Jamshedpur", "Dhanbad", "Bokaro Steel City", "Deoghar",
        "Hazaribagh", "Giridih", "Ramgarh", "Phusro", "Medininagar",
        "Chaibasa", "Dumka",
    ],
    "Karnataka": [
        "Bengaluru", "Mysuru", "Mangaluru", "Hubballi", "Dharwad", "Belagavi",
        "Kalaburagi", "Ballari", "Vijayapura", "Shivamogga", "Tumakuru", "Udupi",
        "Davangere", "Hassan", "Raichur", "Bidar", "Chitradurga", "Kolar",
        "Mandya", "Chikkamagaluru",
    ],
    "Kerala": [
        "Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam",
        "Kannur", "Alappuzha", "Palakkad", "Kottayam", "Malappuram",
        "Kasaragod", "Wayanad", "Pathanamthitta", "Idukki",
    ],
    "Madhya Pradesh": [
        "Bhopal", "Indore", "Gwalior", "Jabalpur", "Ujjain", "Sagar", "Dewas",
        "Satna", "Ratlam", "Rewa", "Katni", "Burhanpur", "Khandwa", "Morena",
        "Bhind", "Chhindwara", "Shivpuri", "Vidisha", "Sehore", "Betul",
        "Mandsaur", "Neemuch", "Damoh", "Narmadapuram",
    ],
    "Maharashtra": [
        "Mumbai", "Pune", "Nagpur", "Nashik", "Thane", "Navi Mumbai",
        "Chhatrapati Sambhajinagar", "Solapur", "Kolhapur", "Amravati",
        "Nanded", "Sangli", "Jalgaon", "Akola", "Latur", "Dhule", "Ahmednagar",
        "Satara", "Chandrapur", "Parbhani", "Ratnagiri", "Wardha", "Yavatmal",
    ],
    "Manipur": [
        "Imphal", "Thoubal", "Bishnupur", "Churachandpur", "Kakching",
        "Ukhrul", "Senapati", "Tamenglong",
    ],
    "Meghalaya": [
        "Shillong", "Tura", "Jowai", "Nongpoh", "Williamnagar", "Baghmara",
        "Resubelpara", "Mairang",
    ],
    "Mizoram": [
        "Aizawl", "Lunglei", "Champhai", "Serchhip", "Kolasib", "Lawngtlai",
        "Saitual", "Mamit",
    ],
    "Nagaland": [
        "Kohima", "Dimapur", "Mokokchung", "Tuensang", "Wokha", "Mon",
        "Zunheboto", "Phek", "Peren",
    ],
    "Odisha": [
        "Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Sambalpur", "Puri",
        "Balasore", "Baripada", "Bhadrak", "Jharsuguda", "Angul", "Jeypore",
        "Bargarh", "Rayagada", "Kendujhar", "Paradip",
    ],
    "Punjab": [
        "Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda", "Mohali",
        "Hoshiarpur", "Pathankot", "Moga", "Batala", "Firozpur", "Sangrur",
        "Kapurthala", "Faridkot", "Abohar", "Malerkotla",
    ],
    "Rajasthan": [
        "Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner", "Alwar",
        "Bharatpur", "Sikar", "Bhilwara", "Pali", "Sri Ganganagar",
        "Hanumangarh", "Tonk", "Kishangarh", "Beawar", "Chittorgarh", "Barmer",
        "Jaisalmer", "Dausa",
    ],
    "Sikkim": [
        "Gangtok", "Namchi", "Gyalshing", "Mangan", "Singtam", "Rangpo",
        "Jorethang", "Pakyong",
    ],
    "Tamil Nadu": [
        "Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem",
        "Tiruppur", "Erode", "Tirunelveli", "Vellore", "Thoothukudi",
        "Dindigul", "Thanjavur", "Hosur", "Nagercoil", "Kanchipuram", "Karur",
        "Cuddalore", "Sivakasi", "Tiruvannamalai", "Namakkal",
    ],
    "Telangana": [
        "Hyderabad", "Warangal", "Karimnagar", "Nizamabad", "Khammam",
        "Ramagundam", "Mahbubnagar", "Suryapet", "Siddipet", "Miryalaguda",
        "Adilabad", "Jagtial", "Mancherial", "Kothagudem", "Nalgonda",
    ],
    "Tripura": [
        "Agartala", "Udaipur", "Dharmanagar", "Kailashahar", "Belonia",
        "Khowai", "Ambassa", "Sabroom",
    ],
    "Uttar Pradesh": [
        "Lucknow", "Kanpur", "Noida", "Greater Noida", "Ghaziabad", "Agra",
        "Varanasi", "Prayagraj", "Meerut", "Bareilly", "Aligarh", "Moradabad",
        "Gorakhpur", "Jhansi", "Mathura", "Ayodhya", "Firozabad", "Saharanpur",
        "Muzaffarnagar", "Etawah", "Rampur", "Shahjahanpur", "Sitapur", "Unnao",
        "Jaunpur", "Mirzapur", "Ballia", "Banda", "Fatehpur", "Hapur",
        "Bulandshahr", "Amroha",
    ],
    "Uttarakhand": [
        "Dehradun", "Haridwar", "Roorkee", "Haldwani", "Rudrapur", "Kashipur",
        "Rishikesh", "Nainital", "Almora", "Pithoragarh", "Ramnagar", "Srinagar",
    ],
    "West Bengal": [
        "Kolkata", "Howrah", "Durgapur", "Asansol", "Siliguri", "Bardhaman",
        "Kharagpur", "Haldia", "Malda", "Jalpaiguri", "Darjeeling",
        "Berhampore", "Krishnanagar", "Cooch Behar", "Bankura", "Purulia",
    ],

    // Union Territories
    "Andaman and Nicobar Islands": [
        "Port Blair", "Diglipur", "Rangat", "Mayabunder", "Car Nicobar",
    ],
    "Chandigarh": ["Chandigarh"],
    "Dadra and Nagar Haveli and Daman and Diu": ["Daman", "Diu", "Silvassa"],
    "Delhi": [
        "New Delhi", "Delhi", "Dwarka", "Rohini", "Saket", "Karol Bagh",
        "Janakpuri", "Narela",
    ],
    "Jammu and Kashmir": [
        "Srinagar", "Jammu", "Anantnag", "Baramulla", "Udhampur", "Kathua",
        "Sopore", "Pulwama", "Rajouri", "Doda", "Kishtwar",
    ],
    "Ladakh": ["Leh", "Kargil", "Nubra", "Drass"],
    "Lakshadweep": [
        "Kavaratti", "Agatti", "Andrott", "Minicoy", "Amini", "Kalpeni",
    ],
    "Puducherry": ["Puducherry", "Karaikal", "Mahe", "Yanam"],
};

/* =========================
   SMALL HELPERS
========================= */
const UsersIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const warn = (title, text) =>
    Swal.fire({ icon: "warning", title, text, confirmButtonText: "OK" });

const normalizePhone = (phone) => phone?.replace(/\D/g, "").slice(-10);

/* =========================
   COMPONENT
========================= */
function TrainerRegister() {
    const navigate = useNavigate();

    const [error, setError] = useState("");
    const [resume, setResume] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        secondaryPhone: "",
        email: "",
        secondaryEmail: "",
        age: "",
        experience: "",
        state: "",
        city: "",
        pincode: "",
        skills: [],
        professionalType: "IT / Corporate Trainer",
        classes: [],
        education: "",
        board: "",
        teachingMode: [],
        examSpecialization: [],
        source: "",
        rate: "",
        remark: "",
    });

    const technologyOptions =
        technologyOptionsByType[formData.professionalType] || [];

    // SkillPicker sections: engineering -> branch-wise, baaki -> single list
    const skillSections = useMemo(
        () =>
            formData.professionalType === TECHNICAL_TYPE
                ? engineeringSections
                : [
                      {
                          id: "all",
                          title: formData.professionalType,
                          blocks: [{ heading: null, items: technologyOptions }],
                      },
                  ],
        [formData.professionalType, technologyOptions]
    );

    /* ---------- handlers ---------- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "state" ? { city: "" } : {}),
        }));
        setError("");
    };

    const handleArrayToggle = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? prev[field].filter((item) => item !== value)
                : [...prev[field], value],
        }));
        setError("");
    };

    const handleProfessionalTypeChange = (e) => {
        const professionalType = e.target.value;
        setFormData((prev) => ({
            ...prev,
            professionalType,
            skills: [],
            classes: [],
            examSpecialization: [],
            board: "",
        }));
        setError("");
    };

    const removeSkill = (skill) => {
        setFormData((prev) => ({
            ...prev,
            skills: prev.skills.filter((item) => item !== skill),
        }));
    };

    const clearAllSkills = () => {
        setFormData((prev) => ({ ...prev, skills: [] }));
    };

    const removeResume = () => setResume(null);

    /* ---------- resume upload + PDF parser ---------- */
    const uploadResume = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        const validExtension = [".pdf", ".doc", ".docx"].some((ext) =>
            fileName.endsWith(ext)
        );

        if (!validExtension) {
            warn("Invalid File", "Only PDF, DOC and DOCX files are allowed.");
            e.target.value = "";
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            warn("File Too Large", "Resume size must not exceed 5 MB.");
            e.target.value = "";
            return;
        }

        setResume(file);
        setError("");

        // PDF parser only
        if (file.type !== "application/pdf" && !fileName.endsWith(".pdf")) {
            return;
        }

        try {
            const buffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({
                data: new Uint8Array(buffer),
            }).promise;

            let fullText = "";
            for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
                const page = await pdf.getPage(pageNumber);
                const content = await page.getTextContent();
                fullText += content.items.map((item) => item.str).join(" ") + " ";
            }

            const cleanText = fullText.replace(/\s+/g, " ").trim();

            // Aapka existing email / phone / name / skill detection code
            // yahin cleanText ke upar continue rahega.
            void cleanText;
        } catch (parseError) {
            console.error("PDF parsing error:", parseError);
            Swal.fire({
                icon: "warning",
                title: "PDF Reading Issue",
                text: "Resume uploaded, but some information could not be extracted automatically.",
                confirmButtonText: "OK",
            });
        }
    };

    /* ---------- submit ---------- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Name
        if (!formData.name.trim()) {
            return warn("Full Name Required", "Please enter your full name.");
        }
        if (formData.name.trim().length < 3) {
            return warn("Invalid Name", "Name must contain at least 3 characters.");
        }

        // Phone
        if (!formData.phone.trim()) {
            return warn("Phone Number Required", "Please enter your primary phone number.");
        }
        if (!PHONE_REGEX.test(formData.phone.trim())) {
            return warn("Invalid Phone Number", "Please enter a valid 10-digit Indian mobile number.");
        }

        // Secondary phone
        if (formData.secondaryPhone.trim()) {
            if (!PHONE_REGEX.test(formData.secondaryPhone.trim())) {
                return warn("Invalid Secondary Phone", "Please enter a valid 10-digit secondary mobile number.");
            }
            if (formData.secondaryPhone.trim() === formData.phone.trim()) {
                return warn("Duplicate Phone Number", "Primary and secondary phone numbers cannot be the same.");
            }
        }

        // Email
        if (!formData.email.trim()) {
            return warn("Email Required", "Please enter your primary email address.");
        }
        if (!EMAIL_REGEX.test(formData.email.trim())) {
            return warn("Invalid Email", "Please enter a valid email address.");
        }

        // Secondary email
        if (formData.secondaryEmail.trim()) {
            if (!EMAIL_REGEX.test(formData.secondaryEmail.trim())) {
                return warn("Invalid Secondary Email", "Please enter a valid secondary email address.");
            }
            if (
                formData.secondaryEmail.trim().toLowerCase() ===
                formData.email.trim().toLowerCase()
            ) {
                return warn("Duplicate Email", "Primary and secondary email addresses cannot be the same.");
            }
        }

        // Age
        if (formData.age) {
            const age = Number(formData.age);
            if (age < 18 || age > 80) {
                return warn("Invalid Age", "Age must be between 18 and 80 years.");
            }
        }

        // Experience
        if (formData.experience) {
            const experience = Number(formData.experience);
            if (experience < 0 || experience > 50) {
                return warn("Invalid Experience", "Experience must be between 0 and 50 years.");
            }
        }

        // State / City
        if (formData.state && !formData.city) {
            return warn("City Required", "Please select a city for the selected state.");
        }

        // Pincode
        if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
            return warn("Invalid Pincode", "Pincode must contain exactly 6 digits.");
        }

        // Skills
        if (!formData.skills || formData.skills.length === 0) {
            return warn(
                "Subject / Expertise Required",
                "Please select at least one technology, subject, or area of expertise."
            );
        }

        // Home tutor classes
        if (formData.professionalType === "Home Tutor" && formData.classes.length === 0) {
            return warn("Class Level Required", "Please select at least one class level for home tuition.");
        }

        // Rate
        if (formData.rate) {
            const rate = Number(formData.rate);
            if (rate < 0 || rate > 1000000) {
                return warn("Invalid Rate", "Expected rate must be between ₹0 and ₹10,00,000 per day.");
            }
        }

        // Source
        if (formData.source && formData.source.length > 50) {
            return warn("Invalid Source", "Source cannot exceed 50 characters.");
        }

        // Remark
        if (formData.remark && formData.remark.length > 1000) {
            return warn("Remark Too Long", "Remarks cannot exceed 1000 characters.");
        }

        // Resume
        if (resume) {
            const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ];
            const fileName = resume.name.toLowerCase();
            const validType =
                allowedTypes.includes(resume.type) ||
                [".pdf", ".doc", ".docx"].some((ext) => fileName.endsWith(ext));

            if (!validType) {
                return warn("Invalid Resume", "Please upload a PDF, DOC or DOCX file.");
            }
            if (resume.size > 5 * 1024 * 1024) {
                return warn("Resume Too Large", "Resume size must not exceed 5 MB.");
            }
        }

        try {
            setLoading(true);

            // Existing trainers (duplicate check)
            const existingResponse = await getAllTrainer();
            const trainers = Array.isArray(existingResponse?.data)
                ? existingResponse.data
                : [];

            const emailExists = trainers.some(
                (trainer) =>
                    trainer.email?.trim().toLowerCase() ===
                    formData.email.trim().toLowerCase()
            );

            if (emailExists) {
                setLoading(false);
                Swal.fire({
                    icon: "error",
                    title: "Email Already Registered",
                    text: "A trainer with this email address is already registered.",
                    confirmButtonText: "OK",
                });
                return;
            }

            const phoneExists = trainers.some(
                (trainer) =>
                    normalizePhone(trainer.phone) === normalizePhone(formData.phone)
            );

            if (phoneExists) {
                setLoading(false);
                Swal.fire({
                    icon: "error",
                    title: "Phone Already Registered",
                    text: "A trainer with this phone number is already registered.",
                    confirmButtonText: "OK",
                });
                return;
            }

            // Confirmation
            const confirmation = await Swal.fire({
                icon: "question",
                title: "Submit Registration?",
                text: "Please confirm that all the information provided is correct.",
                showCancelButton: true,
                confirmButtonText: "Yes, Register",
                cancelButtonText: "Review",
                reverseButtons: true,
            });

            if (!confirmation.isConfirmed) {
                setLoading(false);
                return;
            }

            const trainerData = {
                name: formData.name.trim(),
                phone: formData.phone.trim(),
                secondaryPhone: formData.secondaryPhone.trim(),
                email: formData.email.trim(),
                secondaryEmail: formData.secondaryEmail.trim(),
                age: formData.age,
                experience: formData.experience,
                state: formData.state,
                city: formData.city,
                pincode: formData.pincode,
                skills: formData.skills.join(", "),
                professionalType: formData.professionalType,
                subjects: formData.skills.join(", "),
                classes: formData.classes.join(", "),
                education: formData.education,
                board: formData.board,
                teachingMode: formData.teachingMode.join(", "),
                examSpecialization: formData.examSpecialization.join(", "),
                source: formData.source,
                rate: formData.rate,
                remark: formData.remark,
            };

            const multipartData = new FormData();
            multipartData.append(
                "trainer",
                new Blob([JSON.stringify(trainerData)], {
                    type: "application/json",
                })
            );

            if (resume) {
                multipartData.append("resume", resume);
            }

            await saveTrainer(multipartData);

            await Swal.fire({
                icon: "success",
                title: "Registration Successful!",
                text: "Your trainer profile has been registered successfully.",
                confirmButtonText: "Continue",
            });

            navigate("/");
        } catch (err) {
            console.error("Trainer registration error:", err);

            const apiMessage =
                err?.response?.data?.message ||
                err?.response?.data ||
                err?.message ||
                "Something went wrong. Please try again.";

            Swal.fire({
                icon: "error",
                title: "Registration Failed",
                text:
                    typeof apiMessage === "string"
                        ? apiMessage
                        : "Unable to register trainer.",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    const isHome = formData.professionalType === "Home Tutor";
    const isNeet = formData.professionalType === "NEET Faculty";
    const isJee = formData.professionalType === "JEE Faculty";

    return (
        <div className="trainer-register-page">
            {/* ================= HERO ================= */}
         
<section className="trainer-register-hero">
    <div className="trainer-hero-content">
        <h1 className="trainer-hero-title">
            Share Your Knowledge.
            <span>Inspire Future Success.</span>
        </h1>

        <p className="trainer-hero-subtitle">
            Join the CertED Educator Network and connect with
            schools, colleges, universities, companies, and learners
            looking for skilled teachers, faculty members, home tutors,
            corporate trainers, and competitive exam experts.
        </p>

        <div className="trainer-hero-points">
            <div className="trainer-hero-point">
                <CheckCircle2 size={18} />
                <span>Teaching & Training Opportunities</span>
            </div>

            <div className="trainer-hero-point">
                <CheckCircle2 size={18} />
                <span>Online, Offline & Home Tutoring</span>
            </div>

            <div className="trainer-hero-point">
                <CheckCircle2 size={18} />
                <span>Academic & Professional Growth</span>
            </div>
        </div>
    </div>

   <div className="trainer-hero-visual">
    <div className="trainer-visual-orbit trainer-orbit-one"></div>
    <div className="trainer-visual-orbit trainer-orbit-two"></div>
    <div className="trainer-visual-orbit trainer-orbit-three"></div>

    <div className="trainer-visual-main">
        <GraduationCap size={64} />
        <div>
            <strong>CERTED</strong>
            <span>EDUCATOR NETWORK</span>
        </div>
    </div>

<div className="trainer-floating-card trainer-card-one">
    <div className="trainer-floating-icon">
        <Building2 size={20} />
    </div>
    <div>
        <strong>Corporate Trainer</strong>
        <span>Professional Skills</span>
    </div>
</div>

<div className="trainer-floating-card trainer-card-two">
    <div className="trainer-floating-icon">
        <UsersIcon size={20} />
    </div>
    <div>
        <strong>Home Tutor</strong>
        <span>Personal Learning</span>
    </div>
</div>

<div className="trainer-floating-card trainer-card-three">
    <div className="trainer-floating-icon">
        <Globe2 size={20} />
    </div>
    <div>
        <strong>NEET / JEE Faculty</strong>
        <span>Exam Preparation</span>
    </div>
</div>

<div className="trainer-floating-card trainer-card-four">
    <div className="trainer-floating-icon">
        <Building2 size={20} />
    </div>
    <div>
        <strong>College Faculty</strong>
        <span>Higher Education</span>
    </div>
</div>

<div className="trainer-floating-card trainer-card-five">
    <div className="trainer-floating-icon">
        <GraduationCap size={20} />
    </div>
    <div>
        <strong>Technical Trainer</strong>
        <span>IT & Engineering</span>
    </div>
</div>
</div>

</section>

            {/* ================= MAIN ================= */}
            <main className="trainer-register-main">
                <div className="trainer-form-heading">
                    <div className="trainer-section-label">TRAINER REGISTRATION</div>
                    <h2>Tell Us About Your Expertise</h2>
                    <p>
                        Complete your profile with accurate information so we can
                        match you with the right training opportunities.
                    </p>
                </div>

                {error && (
                    <div className="trainer-form-error">
                        <X size={18} />
                        <span>{error}</span>
                    </div>
                )}

                <form className="trainer-registration-form" onSubmit={handleSubmit}>
                    {/* ---------- PERSONAL ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <UserRound size={21} />
                            </div>
                            <div>
                                <h3>Personal Information</h3>
                                <p>Provide your basic contact details.</p>
                            </div>
                        </div>

                        <div className="trainer-form-grid">
                            <div className="trainer-field">
                                <label>Full Name <span>*</span></label>
                                <div className="trainer-input-wrap">
                                    <UserRound size={18} />
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter your full name"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>Primary Phone <span>*</span></label>
                                <div className="trainer-input-wrap">
                                    <Phone size={18} />
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>Secondary Phone</label>
                                <div className="trainer-input-wrap">
                                    <Phone size={18} />
                                    <input
                                        type="tel"
                                        name="secondaryPhone"
                                        value={formData.secondaryPhone}
                                        onChange={handleChange}
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>Primary Email <span>*</span></label>
                                <div className="trainer-input-wrap">
                                    <Mail size={18} />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter email address"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>Secondary Email</label>
                                <div className="trainer-input-wrap">
                                    <Mail size={18} />
                                    <input
                                        type="email"
                                        name="secondaryEmail"
                                        value={formData.secondaryEmail}
                                        onChange={handleChange}
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>Age</label>
                                <div className="trainer-input-wrap">
                                    <UserRound size={18} />
                                    <input
                                        type="number"
                                        name="age"
                                        value={formData.age}
                                        onChange={handleChange}
                                        placeholder="Enter age"
                                        min="18"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ---------- PROFESSIONAL INFO ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <BriefcaseBusiness size={21} />
                            </div>
                            <div>
                                <h3>Professional Information</h3>
                                <p>Tell us about your training experience.</p>
                            </div>
                        </div>

                        <div className="trainer-form-grid">
                            <div className="trainer-field">
                                <label>Total Experience</label>
                                <div className="trainer-input-wrap">
                                    <BriefcaseBusiness size={18} />
                                    <input
                                        type="number"
                                        name="experience"
                                        value={formData.experience}
                                        onChange={handleChange}
                                        placeholder="Years of experience"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>Expected Rate / Day</label>
                                <div className="trainer-input-wrap">
                                    <IndianRupee size={18} />
                                    <input
                                        type="number"
                                        name="rate"
                                        value={formData.rate}
                                        onChange={handleChange}
                                        placeholder="Expected rate per day"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="trainer-field">
                                <label>How did you hear about us?</label>
                                <div className="trainer-input-wrap select-wrap">
                                    <Globe2 size={18} />
                                    <select
                                        name="source"
                                        value={formData.source}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select source</option>
                                        <option value="LinkedIn">LinkedIn</option>
                                        <option value="WhatsApp">WhatsApp</option>
                                        <option value="Reference">Reference</option>
                                        <option value="Website">Website</option>
                                        <option value="Email">Email</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ---------- LOCATION ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <MapPin size={21} />
                            </div>
                            <div>
                                <h3>Location Details</h3>
                                <p>Where are you currently based?</p>
                            </div>
                        </div>

                        <div className="trainer-form-grid">
                            <div className="trainer-field">
                                <label>State</label>
                                <SearchableSelect
                                    name="state"
                                    value={formData.state}
                                    options={Object.keys(states)}
                                    onChange={handleChange}
                                    placeholder="Select state"
                                    searchPlaceholder="Search state..."
                                    icon={<MapPin size={18} />}
                                />
                            </div>

                            <div className="trainer-field">
                                <label>City</label>
                                <SearchableSelect
                                    name="city"
                                    value={formData.city}
                                    options={formData.state ? states[formData.state] || [] : []}
                                    onChange={handleChange}
                                    placeholder="Select city"
                                    searchPlaceholder="Search city..."
                                    icon={<MapPin size={18} />}
                                    disabled={!formData.state}
                                />
                            </div>

                            <div className="trainer-field">
                                <label>Pincode</label>
                                <div className="trainer-input-wrap">
                                    <MapPin size={18} />
                                    <input
                                        type="text"
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={handleChange}
                                        placeholder="Enter pincode"
                                        maxLength="6"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ---------- PROFESSIONAL CATEGORY ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <GraduationCap size={21} />
                            </div>
                            <div>
                                <h3>Professional Category</h3>
                                <p>Choose the type of trainer profile you want to create.</p>
                            </div>
                        </div>

                        <div className="trainer-form-grid">
                            <div className="trainer-form-group">
                                <label htmlFor="professionalType">I am registering as *</label>
                                <select
                                    id="professionalType"
                                    name="professionalType"
                                    value={formData.professionalType}
                                    onChange={handleProfessionalTypeChange}
                                    required
                                >
                                    {professionalTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="trainer-form-group">
                                <label htmlFor="education">Highest Qualification</label>
                                <input
                                    id="education"
                                    name="education"
                                    type="text"
                                    value={formData.education}
                                    onChange={handleChange}
                                    placeholder="e.g. M.Sc., B.Ed., B.Tech, M.Tech"
                                    maxLength={150}
                                />
                            </div>

                            {isHome && (
                                <div className="trainer-form-group">
                                    <label htmlFor="board">Board / Curriculum</label>
                                    <select
                                        id="board"
                                        name="board"
                                        value={formData.board}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select board</option>
                                        <option value="CBSE">CBSE</option>
                                        <option value="ICSE">ICSE</option>
                                        <option value="State Board">State Board</option>
                                        <option value="IB">IB</option>
                                        <option value="IGCSE">IGCSE</option>
                                        <option value="Multiple Boards">Multiple Boards</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {(isHome || isNeet || isJee) && (
                            <div className="trainer-form-group" style={{ marginTop: 18 }}>
                                <label>Classes / Level {isHome ? "*" : "(optional)"}</label>
                                <div className="trainer-technology-grid">
                                    {classOptions.map((item) => (
                                        <CheckOption
                                            key={item}
                                            label={item}
                                            selected={formData.classes.includes(item)}
                                            onToggle={() => handleArrayToggle("classes", item)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {(isNeet || isJee) && (
                            <div className="trainer-form-group" style={{ marginTop: 18 }}>
                                <label>Exam Specialization</label>
                                <div className="trainer-technology-grid">
                                    {examOptions
                                        .filter((item) =>
                                            isNeet
                                                ? item.startsWith("NEET")
                                                : item.startsWith("JEE")
                                        )
                                        .map((item) => (
                                            <CheckOption
                                                key={item}
                                                label={item}
                                                selected={formData.examSpecialization.includes(item)}
                                                onToggle={() =>
                                                    handleArrayToggle("examSpecialization", item)
                                                }
                                            />
                                        ))}
                                </div>
                            </div>
                        )}

                        <div className="trainer-form-group" style={{ marginTop: 18 }}>
                            <label>Teaching Mode</label>
                            <div className="trainer-technology-grid">
                                {teachingModeOptions.map((item) => (
                                    <CheckOption
                                        key={item}
                                        label={item}
                                        selected={formData.teachingMode.includes(item)}
                                        onToggle={() => handleArrayToggle("teachingMode", item)}
                                    />
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ---------- SKILLS ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <GraduationCap size={21} />
                            </div>
                            <div>
                                <h3>Areas of Expertise</h3>
                                <p>Search and select all technologies, subjects, or academic areas you can teach.</p>
                            </div>
                        </div>

                        <div className="trainer-skills-container">
                            <SkillPicker
                                key={formData.professionalType}
                                sections={skillSections}
                                selected={formData.skills}
                                onToggle={(skill) => handleArrayToggle("skills", skill)}
                                onClearAll={clearAllSkills}
                            />

                            {formData.skills.length > 0 && (
                                <div className="trainer-selected-skills">
                                    <div className="trainer-selected-title">
                                        <CheckCircle2 size={17} />
                                        <span>Selected Skills</span>
                                        <b>{formData.skills.length}</b>
                                    </div>

                                    <div className="trainer-skill-chips">
                                        {formData.skills.map((skill) => (
                                            <div className="trainer-skill-chip" key={skill}>
                                                <span>{skill}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeSkill(skill)}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ---------- ADDITIONAL INFO ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <FileText size={21} />
                            </div>
                            <div>
                                <h3>Additional Information</h3>
                                <p>Add anything else we should know.</p>
                            </div>
                        </div>

                        <div className="trainer-field trainer-full-field">
                            <label>Remarks</label>
                            <textarea
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                placeholder="Tell us about your training experience, certifications, preferred training modes, availability, etc."
                                rows="5"
                            />
                        </div>
                    </section>

                    {/* ---------- RESUME ---------- */}
                    <section className="trainer-form-card">
                        <div className="trainer-card-heading">
                            <div className="trainer-card-icon">
                                <FileText size={21} />
                            </div>
                            <div>
                                <h3>Upload Your Resume</h3>
                                <p>Upload your latest professional resume.</p>
                            </div>
                        </div>

                        <div className="trainer-resume-area">
                            {!resume ? (
                                <label
                                    htmlFor="trainer-resume-upload"
                                    className="trainer-upload-box"
                                >
                                    <div className="trainer-upload-icon">
                                        <Upload size={28} />
                                    </div>

                                    <div className="trainer-upload-content">
                                        <strong>Upload your resume</strong>
                                        <span>PDF, DOC or DOCX files</span>
                                    </div>

                                    <div className="trainer-upload-button">
                                        Choose File
                                        <ArrowRight size={16} />
                                    </div>

                                    <input
                                        id="trainer-resume-upload"
                                        type="file"
                                        accept=".pdf,.doc,.docx,application/pdf"
                                        onChange={uploadResume}
                                        hidden
                                    />
                                </label>
                            ) : (
                                <div className="trainer-resume-selected">
                                    <div className="trainer-resume-file-icon">
                                        <FileText size={26} />
                                    </div>

                                    <div className="trainer-resume-info">
                                        <strong>{resume.name}</strong>
                                        <span>
                                            {(resume.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        className="trainer-remove-resume"
                                        onClick={removeResume}
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ---------- SUBMIT ---------- */}
                    <div className="trainer-submit-area">
                        <div className="trainer-submit-note">
                            <ShieldCheck size={19} />
                            <span>Your information is securely submitted to the CertED team.</span>
                        </div>

                        <button
                            type="submit"
                            className="trainer-register-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="trainer-loader"></span>
                                    Registering...
                                </>
                            ) : (
                                <>
                                    Register as Trainer
                                    <ArrowRight size={19} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}

export default TrainerRegister;